import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaShareAlt, FaWhatsapp, FaInfoCircle, FaTimes, FaSearch } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { whatsappHrefWithText } from '../utils/contactLinks';
import { getPublicKafalaOrphelins } from '../services/kafalaService';
import { usePublicPaths } from '../routes/publicPaths';
import { resolveKafalaPageContent } from '../config/kafalaPageDefaults';
import KafalaOrphanAvatar, { displayOrphelinName, formatOrphelinDate } from './KafalaOrphanAvatar';
import './KafalaOrphelinsWaiting.css';

const EMPTY_FILTERS = { sexe: '', age: '', ville: '' };
const AGE_RANGES = ['under_5', '5_10', '10_15', 'over_15'];

const ageOf = (orphelin) => {
  if (!orphelin?.date_naissance) return null;
  const date = new Date(`${orphelin.date_naissance}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - date.getFullYear();
  const monthDiff = now.getMonth() - date.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < date.getDate())) {
    age -= 1;
  }
  return age;
};

const matchesAge = (orphelin, range) => {
  if (!range) return true;
  const age = ageOf(orphelin);
  if (age == null) return false;
  if (range === 'under_5') return age < 5;
  if (range === '5_10') return age >= 5 && age < 10;
  if (range === '10_15') return age >= 10 && age < 15;
  if (range === 'over_15') return age >= 15;
  return true;
};

const KafalaOrphelinsWaiting = ({ program }) => {
  const { t } = useTranslation();
  const paths = usePublicPaths();
  const settings = useSiteSettings();
  const page = resolveKafalaPageContent(program);
  const [orphelins, setOrphelins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const whatsappNumber = settings.whatsapp || settings.phone || '';

  useEffect(() => {
    getPublicKafalaOrphelins()
      .then((data) => setOrphelins(Array.isArray(data) ? data : []))
      .catch(() => setOrphelins([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!rulesOpen) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setRulesOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rulesOpen]);

  const cities = useMemo(() => {
    const unique = [...new Set(orphelins.map((item) => item.ville).filter(Boolean))];
    return unique.sort((a, b) => a.localeCompare(b, 'ar'));
  }, [orphelins]);

  const filtered = useMemo(
    () => orphelins.filter((orphelin) => {
      if (applied.sexe && orphelin.sexe !== applied.sexe) return false;
      if (applied.ville && orphelin.ville !== applied.ville) return false;
      return matchesAge(orphelin, applied.age);
    }),
    [orphelins, applied]
  );

  const requestHref = (orphelin) => {
    const name = displayOrphelinName(orphelin);
    return whatsappHrefWithText(whatsappNumber, `أريد كفالة هدا اليتيم : ${name}`);
  };

  const shareOrphelin = async (orphelin) => {
    const url = `${window.location.origin}${paths.kafalaOrphelin(orphelin.id)}`;
    const title = `كفالة ${displayOrphelinName(orphelin)}`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text: title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
    } catch {
      // ignore share cancel
    }
  };

  const applyFilters = () => {
    setApplied({ ...draft });
    setFiltersOpen(false);
  };

  const clearFilters = () => {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
  };

  return (
    <section id="waiting" className="program-section program-section--alt kafala-waiting">
      <div className="program-section__inner">
        <h2 className="program-section__title kafala-waiting__title">{t('kafalaPublic.waitingTitle')}</h2>

        <div className="kafala-filter">
          <div className="kafala-filter__banner">
            <p>{page.banner}</p>
            <button type="button" className="kafala-filter__rules-btn" onClick={() => setRulesOpen(true)}>
              <FaInfoCircle />
              {t('kafalaPublic.rulesButton')}
            </button>
          </div>

          {filtersOpen ? (
            <div className="kafala-filter__panel">
              <header className="kafala-filter__panel-head">
                <h3>{t('kafalaPublic.search')}</h3>
                <button type="button" className="kafala-filter__close" onClick={() => setFiltersOpen(false)}>
                  {t('kafalaPublic.close')} <FaTimes />
                </button>
              </header>
              <div className="kafala-filter__fields">
                <label>
                  {t('kafalaPublic.gender')}
                  <select value={draft.sexe} onChange={(e) => setDraft({ ...draft, sexe: e.target.value })}>
                    <option value="">{t('kafalaPublic.all')}</option>
                    <option value="M">{t('kafalaForm.gender_M')}</option>
                    <option value="F">{t('kafalaForm.gender_F')}</option>
                  </select>
                </label>
                <label>
                  {t('kafalaPublic.age')}
                  <select value={draft.age} onChange={(e) => setDraft({ ...draft, age: e.target.value })}>
                    <option value="">{t('kafalaPublic.all')}</option>
                    {AGE_RANGES.map((range) => (
                      <option key={range} value={range}>{t(`kafalaForm.age_${range}`)}</option>
                    ))}
                  </select>
                </label>
                <label>
                  {t('kafalaPublic.region')}
                  <select value={draft.ville} onChange={(e) => setDraft({ ...draft, ville: e.target.value })}>
                    <option value="">{t('kafalaPublic.all')}</option>
                    {cities.map((city) => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="kafala-filter__actions">
                <button type="button" className="kafala-filter__submit" onClick={applyFilters}>
                  {t('kafalaPublic.search')}
                </button>
                <button type="button" className="kafala-filter__reset" onClick={clearFilters}>
                  {t('kafalaPublic.clearAll')}
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="kafala-filter__searchbar" onClick={() => setFiltersOpen(true)}>
              <span className="kafala-filter__search-btn">
                <FaSearch /> {t('kafalaPublic.search')}
              </span>
            </button>
          )}
        </div>

        {loading ? (
          <p className="program-section__empty">…</p>
        ) : orphelins.length === 0 ? (
          <p className="program-section__empty">{t('kafalaPublic.empty')}</p>
        ) : filtered.length === 0 ? (
          <p className="program-section__empty">{t('kafalaPublic.noFilterResults')}</p>
        ) : (
          <div className="kafala-waiting__grid">
            {filtered.map((orphelin) => {
              const href = requestHref(orphelin);
              const detailPath = paths.kafalaOrphelin(orphelin.id);
              return (
                <article key={orphelin.id} className="kafala-orphan-card">
                  <header className="kafala-orphan-card__head">
                    <button
                      type="button"
                      className="kafala-orphan-card__share"
                      onClick={() => shareOrphelin(orphelin)}
                      aria-label={t('kafalaPublic.share')}
                    >
                      <FaShareAlt />
                    </button>
                    <h3>{displayOrphelinName(orphelin)}</h3>
                  </header>
                  <Link to={detailPath} className="kafala-orphan-card__media">
                    <KafalaOrphanAvatar orphelin={orphelin} />
                  </Link>
                  <dl className="kafala-orphan-card__meta">
                    <div>
                      <dt>{t('kafalaPublic.birthDate')}</dt>
                      <dd>{formatOrphelinDate(orphelin.date_naissance)}</dd>
                    </div>
                    <div>
                      <dt>{t('kafalaPublic.city')}</dt>
                      <dd>{orphelin.ville || t('kafalaPublic.unknownCity')}</dd>
                    </div>
                    <div>
                      <dt>{t('kafalaPublic.orphanSince')}</dt>
                      <dd>{formatOrphelinDate(orphelin.date_inscription)}</dd>
                    </div>
                  </dl>
                  <div className="kafala-orphan-card__actions">
                    <Link className="kafala-orphan-card__cta" to={detailPath}>
                      {t('kafalaPublic.moreInfo')}
                    </Link>
                    {href ? (
                      <a
                        className="kafala-orphan-card__cta kafala-orphan-card__cta--wa"
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FaWhatsapp /> {t('kafalaPublic.whatsappCta')}
                      </a>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {rulesOpen ? (
        <div className="kafala-rules-modal" onClick={() => setRulesOpen(false)} role="presentation">
          <div
            className="kafala-rules-modal__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="kafala-rules-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="kafala-rules-modal__x"
              onClick={() => setRulesOpen(false)}
              aria-label={t('kafalaPublic.close')}
            >
              <FaTimes />
            </button>
            <h3 id="kafala-rules-title">{page.conditionsTitle}</h3>
            <div className="kafala-rules-modal__body">
              {page.conditions.map((item, index) => (
                <div key={`${item.title}-${index}`}>
                  {item.title ? <strong>{item.title}</strong> : null}
                  {item.text ? <p>{item.text}</p> : null}
                </div>
              ))}
            </div>
            <button type="button" className="kafala-rules-modal__close" onClick={() => setRulesOpen(false)}>
              {t('kafalaPublic.close')}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default KafalaOrphelinsWaiting;
